import { FULL_SCREEN_SETUP_ROUTES } from '@/lib/constants/full-screen-routes';

/**
 * **Whether a path may be remembered as the account sidebar's "Go Back"
 * destination** (VALE-1473).
 *
 * `AccountReturnPathTracker` records the last non-`/account` page the user was
 * on, and the Go Back button pushes it. Most pages qualify; two kinds do not,
 * for the same underlying reason — they are **transient flows with an exit
 * condition**, so returning to one after leaving it is not "back where I was",
 * it is being put back into something the user had finished with.
 *
 * ## Why this is a function and not two conditions at the call site
 *
 * The rule had one member (`/integrations/`) and a comment explaining the
 * category, and the correspondence-setup flow — an obvious second member — was
 * never added to it. The result was a closed circuit: the review route's Close
 * lands on `/account/profile`, which is where the Go Back button lives, so Go
 * Back returned the user to the flow, Close returned them to the profile, and
 * there was no way out to the work they had been doing.
 *
 * Extracted so the membership is one list with one test over it, rather than an
 * inline predicate nothing can assert.
 */

/**
 * Prefixes whose whole subtree is a transient setup or auth flow.
 *
 * `/integrations/` is the original member: `/integrations/smokeball/configure`
 * errors once Smokeball is already connected, so returning to it shows the user
 * a failure rather than a page.
 */
const TRANSIENT_PATH_PREFIXES = ['/integrations/'];

/**
 * @param pathname - The path being considered, without its query string.
 *
 * The correspondence-setup routes are excluded by **exact membership of
 * {@link FULL_SCREEN_SETUP_ROUTES}**, not by an `/onboarding` prefix, and the
 * difference is the whole point: `/onboarding/get-started` is an ordinary
 * dashboard page a user can sensibly come back to, and a prefix match would
 * make the hub unreachable by Go Back. That set is already reconciled against
 * the routes on disk by `__tests__/full-screen-routes-registry.test.ts`, so a
 * future sub-route is classified once and both consumers follow.
 */
export function isEligibleReturnPath(pathname: string | null): boolean {
  if (!pathname) return false;
  if (pathname.startsWith('/account')) return false;
  if (TRANSIENT_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return false;
  }
  if (FULL_SCREEN_SETUP_ROUTES.includes(pathname)) return false;
  return true;
}
